// ═══════════════════════════════════════════════════════════════════════════
// grainHorizon.js — how long a working order on THIS board actually has, and
// whether the board can carry a working order at all.
//
// ── WHAT IT REPLACES, AND WHY THAT WAS A FUNCTIONAL DEFECT ─────────────────
// grainTargets.js's grainBoardHorizon() is:
//
//     const days = (new Date(contractDate) - new Date()) / 86400000;
//     return Math.max(15, Math.min(126, Math.round(days * 0.69)));
//
// Measured as of 2026-08-28:
//
//   board                          true trading days   grainBoardHorizon
//   Sep 2026 (4 calendar days out)         3                  15
//   1 day out                              1                  15
//   Jul 2026 — EXPIRED 58 days ago       -40                  15
//   Mar 2026 — EXPIRED 180 days ago     -124                  15
//   missing contract date                  —                 126
//
// Three separate failures:
//   1. THE 15-DAY FLOOR MANUFACTURES TIME. A board days from the end of its
//      pricing window is presented as having three weeks to work, which is
//      exactly the claim Target 1's "attainable" rests on.
//   2. AN EXPIRED BOARD IS INDISTINGUISHABLE FROM A LIVE ONE. A contract six
//      months dead still reports 15 trading days of working time.
//   3. A MISSING CONTRACT DATE RETURNS THE MAXIMUM, 126. Uncertainty is
//      converted into maximum confidence — the one direction that must never
//      happen. Kevin, 2026-08-28: "Missing dates defaulting to 126 days is
//      unacceptable because uncertainty is being converted into maximum
//      confidence."
//
// And `new Date()` sits inside a calculation the rest of the tool treats as
// reproducible, so the same inputs give different answers on different days
// with nothing recording which day was used.
//
// ── THE REPLACEMENT ────────────────────────────────────────────────────────
// Four states, an explicit `asOf`, and no floor. A board that cannot carry a
// working order says so instead of being given a horizon.
//
// PURE — no fetch, no React, no Supabase, no Date.now()/new Date().
// ═══════════════════════════════════════════════════════════════════════════

import { contractLastTradingDay } from "./grainTargetHistory";

export const BOARD_STATE = {
  ACTIVE: "active",
  CLOSING_SOON: "closing_soon",
  EXPIRED: "expired",
  UNKNOWN: "unknown",
};

export const BOARD_STATE_LABEL = {
  [BOARD_STATE.ACTIVE]: "Active",
  [BOARD_STATE.CLOSING_SOON]: "Closing soon",
  [BOARD_STATE.EXPIRED]: "Closed / expired",
  [BOARD_STATE.UNKNOWN]: "Unknown — incomplete contract data",
};

// STATED PARAMETERS, not economic thresholds.
//   CLOSING_SOON_DAYS — Kevin's own "fewer than approximately five viable
//     trading days remaining" (2026-08-28).
//   TRADING_DAYS_PER_CALENDAR_DAY — 252/365. The same conversion
//     grainBoardHorizon already used; unchanged, so only the floor and the
//     state handling move.
//   MAX_HORIZON — the longest bucket the rally history measures. A board with
//     more time than that is capped, which is a statement about the
//     MEASUREMENT's reach, not about the board.
export const CLOSING_SOON_DAYS = 5;
const TRADING_DAYS_PER_CALENDAR_DAY = 0.69;
export const MAX_HORIZON = 252;

const YMD = /^(\d{4})-(\d{2})-(\d{2})$/;
function daysBetween(fromYmd, toYmd) {
  const a = YMD.exec(String(fromYmd || "").trim()), b = YMD.exec(String(toYmd || "").trim());
  if (!a || !b) return null;
  return Math.round((Date.UTC(+b[1], +b[2] - 1, +b[3]) - Date.UTC(+a[1], +a[2] - 1, +a[3])) / 86400000);
}

// resolveBoardHorizon({ contractDate, asOf, lastTradingDay })
//
//   contractDate  first of the delivery month, 'YYYY-MM-DD'. Missing => UNKNOWN.
//   asOf          the observation date. REQUIRED — missing => UNKNOWN, never
//                 "today", because a horizon that silently reads the clock is
//                 not reproducible and cannot be backtested.
//   lastTradingDay  optional override. Absent, it is derived from the CBOT
//                 grain rule (business day before the 15th of the delivery
//                 month) in grainTargetHistory.js.
//
// Returns tradingDays = null in every state except ACTIVE and CLOSING_SOON. A
// caller must not size, snap or reach-rate anything against a null horizon.
export function resolveBoardHorizon({ contractDate = null, asOf = null, lastTradingDay = null } = {}) {
  const asOfDay = YMD.test(String(asOf || "").trim()) ? String(asOf).trim() : null;
  if (!asOfDay) {
    return {
      state: BOARD_STATE.UNKNOWN, label: BOARD_STATE_LABEL[BOARD_STATE.UNKNOWN],
      tradingDays: null, calendarDays: null, lastTradingDay: null, asOf: null,
      canCarryWorkingOrder: false,
      reason: "no observation date supplied — a horizon that reads the clock inside the calculation is not reproducible",
    };
  }
  const ltd = lastTradingDay || contractLastTradingDay(contractDate);
  if (!ltd) {
    // ⚠ INCOMPLETE DATA IS NOT MAXIMUM CONFIDENCE. The old code returned 126
    // here — the top of the range — so a board with no delivery date on file
    // was given the longest working window the tool can express.
    return {
      state: BOARD_STATE.UNKNOWN, label: BOARD_STATE_LABEL[BOARD_STATE.UNKNOWN],
      tradingDays: null, calendarDays: null, lastTradingDay: null, asOf: asOfDay,
      canCarryWorkingOrder: false,
      reason: "no delivery date on file for this contract — the working window is unknown, which is not the same as long",
    };
  }

  const calendarDays = daysBetween(asOfDay, ltd);
  const rawTradingDays = Math.floor(calendarDays * TRADING_DAYS_PER_CALENDAR_DAY);

  if (calendarDays < 0) {
    return {
      state: BOARD_STATE.EXPIRED, label: BOARD_STATE_LABEL[BOARD_STATE.EXPIRED],
      tradingDays: null, calendarDays, lastTradingDay: ltd, asOf: asOfDay,
      canCarryWorkingOrder: false,
      reason: `this contract stopped trading on ${ltd}, ${Math.abs(calendarDays)} days before ${asOfDay} — no working order can rest on it`,
    };
  }
  if (rawTradingDays <= 0) {
    // Zero remaining eligible trading days is not "a very short horizon"; it is
    // no horizon. The last trading day itself carries no forward window.
    return {
      state: BOARD_STATE.EXPIRED, label: BOARD_STATE_LABEL[BOARD_STATE.EXPIRED],
      tradingDays: 0, calendarDays, lastTradingDay: ltd, asOf: asOfDay,
      canCarryWorkingOrder: false,
      reason: `no eligible trading days remain before this contract stops trading on ${ltd}`,
    };
  }

  const tradingDays = Math.min(MAX_HORIZON, rawTradingDays);
  const state = tradingDays < CLOSING_SOON_DAYS ? BOARD_STATE.CLOSING_SOON : BOARD_STATE.ACTIVE;
  return {
    state, label: BOARD_STATE_LABEL[state],
    tradingDays, calendarDays, lastTradingDay: ltd, asOf: asOfDay,
    canCarryWorkingOrder: true,
    cappedAtMax: rawTradingDays > MAX_HORIZON,
    reason: state === BOARD_STATE.CLOSING_SOON
      ? `about ${tradingDays} eligible trading day${tradingDays === 1 ? "" : "s"} remain before ${ltd} — too few for a target to work in the ordinary course`
      : null,
  };
}

// describeHorizonUse — the disclosure a reach rate must carry when the measured
// bucket is not the horizon that was asked for. Kevin, 2026-08-28: "Do not use
// a 21-day bucket and present it as an exact five-day probability without
// disclosure. If historical data cannot support such a short horizon, withhold
// the reach rate rather than borrowing a longer horizon silently."
//
// `maxSubstitutionRatio` is a STATED PARAMETER: a bucket more than twice the
// requested horizon is not a disclosure problem, it is a different question,
// and the reach rate is withheld instead.
export const MAX_SUBSTITUTION_RATIO = 2;

export function describeHorizonUse({ requested, used, observations = null, contracts = null } = {}) {
  if (requested == null || used == null) {
    return { usable: false, substituted: false, reason: "no horizon resolved" };
  }
  if (used === requested) {
    return { usable: true, substituted: false, requested, used, observations, contracts, note: null };
  }
  const ratio = used / requested;
  if (ratio > MAX_SUBSTITUTION_RATIO) {
    return {
      usable: false, substituted: true, requested, used, observations, contracts,
      reason: `the nearest measured bucket is ${used} trading days against a ${requested}-day window — more than ${MAX_SUBSTITUTION_RATIO}x, so the reach rate is withheld rather than borrowed`,
    };
  }
  return {
    usable: true, substituted: true, requested, used, observations, contracts,
    note: `measured over a ${used}-trading-day window, not ${requested} — the nearest bucket with enough history`,
  };
}
